// slices/chat/addReviewStudySetBlock.ts
import type { Dispatch } from 'redux';
import {
  createReviewStudySet,
  type CreateReviewStudySetArgs,
  type StudySetReviewRefusal,
} from '../../services/studySets';
import type { ChatRootState } from './chatSelectors';
import { blockMessageAdded } from './chatSlice';

export interface AddReviewStudySetBlockArgs extends CreateReviewStudySetArgs {
  /** Client id of the message holding the set the missed question is in. */
  messageId: string;
}

export type AddReviewStudySetBlockResult =
  | {
      ok: true;
      blockId: string;
      /** The thread already held the block (a repeat after a reload). */
      alreadyAdded?: boolean;
    }
  | { ok: false; reason: StudySetReviewRefusal };

/**
 * Study a missed question's linked content as a new set in the thread
 * (ORA-831), with no model turn.
 *
 * The sibling of {@link addReferenceBlock} for a question that lives inside a
 * study set. The add-block route cannot serve those — it names the content from
 * the served block's own record, and inside a set the individual questions have
 * no block id — so the study-set route mints the review set instead, and it is
 * the set (not a citation, not a served block) that names the question.
 *
 * The response names the row it wrote — the empty assistant message the block
 * hangs off — so the thread holds the same id the database does and a re-read
 * replaces the row in place. A backend that does not name one yet leaves the row
 * with no server id at all, which is what keeps regenerate and the answer footer
 * off a row that is not an answer.
 *
 * The row goes at the bottom of the thread, where the server put it: a reload
 * rebuilds it from the set's own createdAt, so inserting it anywhere else would
 * only make it jump on the next read.
 *
 * Resolves rather than throws, so the control that asked can re-enable itself
 * and say why.
 */
export const addReviewStudySetBlock =
  ({ messageId, ...args }: AddReviewStudySetBlockArgs) =>
  async (
    dispatch: Dispatch,
    getState: () => ChatRootState
  ): Promise<AddReviewStudySetBlockResult> => {
    const message = getState().chat.messages[messageId];
    if (!message) return { ok: false, reason: 'unavailable' };

    const { conversationId } = message;
    const created = await createReviewStudySet(args);
    if (!created.ok) return created;

    const { blockId, messageId: serverId } = created;

    // The thread already holds it: an idempotent repeat after a reload, where
    // the server's own message brought the block back. Nothing to insert — the
    // caller scrolls to it.
    if (getState().chat.blocks[blockId]) {
      return { ok: true, blockId, alreadyAdded: true };
    }

    const messageIds =
      getState().chat.conversations[conversationId]?.messageIds ?? [];

    dispatch(
      blockMessageAdded({
        conversationId,
        // The bottom of the thread, which is where the server wrote it.
        afterMessageId: messageIds[messageIds.length - 1] ?? messageId,
        serverId,
        message: {
          id: serverId ?? `review:${blockId}`,
          role: 'assistant',
          text: '',
          blocks: [
            {
              id: blockId,
              kind: 'study_set',
              placement: 'end',
              payload: created.set,
            },
          ],
        },
      })
    );

    return { ok: true, blockId };
  };
